import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Spinner } from "@msuite/ui/spinner";
import { StatusIndicator } from "@msuite/ui/status-indicator";
import { VideoPlayer } from "@msuite/ui/video-player";
import { useVideo } from "@/hooks/use-video";
import { CutVideoModal } from "@/modals/wiki/cut-video-modal";

/** What the card says while the row has not settled — an upload, a cut, or the first encode. */
function processingMessage(video: { status: string; pendingSegments?: unknown }) {
	if (video.status === "uploading") return "videos.uploading" as const;
	if (video.pendingSegments) return "videos.cut_rendering" as const;
	return "videos.processing" as const;
}

/** Props Interface */
interface WikiVideoBlockProps {
	videoId: string;
	isEditable?: boolean;
}

export function WikiVideoBlock({ videoId, isEditable = false }: WikiVideoBlockProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const cutModal = useDisclosure();

	/** Query */
	const { video, playback, isLoading, isError } = useVideo({ videoId });

	/** Guard */
	if (isLoading) {
		return (
			<div className="card flex-center aspect-video w-full">
				<Spinner size={20} />
			</div>
		);
	}

	if (isError || !video) {
		return (
			<div className="card flex items-center gap-2 p-4 text-mauve-11">
				<Icon name="VideoOff" />
				<span>{t("videos.not_found")}</span>
			</div>
		);
	}

	if (video.status === "errored") {
		return (
			<div className="card flex items-center gap-2 p-4 text-mauve-11">
				<Icon name="VideoOff" />
				{/* Without an asset the recording never got as far as being encoded, so what failed
				    was the transfer — and that is the half the author can do something about. */}
				<span>{t(video.muxAssetId ? "videos.processing_failed" : "videos.upload_failed")}</span>
			</div>
		);
	}

	// The row sits at `uploading` from the moment recording starts until Mux has the bytes, so the
	// status alone cannot say which of the two is happening. The upload id can: it is written when
	// the recording stops and the upload is opened, and until then nothing has been sent because
	// nothing has been finished. Saying "processing" through all of it named the one thing that
	// was not happening yet.
	if (video.status === "uploading" && !video.muxUploadId) {
		return (
			<div className="card flex-center flex-col gap-2 aspect-video w-full text-mauve-11">
				<StatusIndicator
					color="red"
					size="md"
					withPing
				/>
				<span>{t("videos.recording_in_progress")}</span>
			</div>
		);
	}

	if (video.status !== "ready" || !playback) {
		return (
			<div className="card flex-center flex-col gap-2 aspect-video w-full text-mauve-11">
				<Spinner size={20} />
				{/* A cut in flight is named as one rather than as generic processing. The author
				    pressed a button and the player disappeared; saying which of the two long-running
				    things is happening is what stops them from pressing it again. */}
				<span>{t(processingMessage(video))}</span>
			</div>
		);
	}

	/** Computed */
	// A selection left on a settled row is a cut that was rendered and failed. The video itself is
	// untouched and playing, so this is a line beside it rather than a state instead of it — but it
	// has to be said, because a cut that fails silently reads as a button that does nothing.
	const hasFailedCut = Boolean(video.pendingSegments);

	/** Render */
	return (
		<div className="v-stack gap-1">
			<VideoPlayer
				playbackId={playback.playbackId}
				tokens={{
					playback: playback.token,
					thumbnail: playback.thumbnailToken,
					storyboard: playback.storyboardToken,
				}}
				aspectRatio={video.aspectRatio}
			/>
			{isEditable && (
				<div className="h-stack justify-end gap-2">
					{hasFailedCut && (
						<span className="text-amber-600 text-xs dark:text-amber-400">
							{t("videos.cut_failed_selection_kept")}
						</span>
					)}
					<Button
						variant="ghost"
						type="button"
						onClick={cutModal.onOpen}
						icon={<Icon name="Scissors" />}
					>
						{t(hasFailedCut ? "videos.cut_retry" : "videos.cut_video")}
					</Button>
				</div>
			)}
			{cutModal.isOpen && (
				<CutVideoModal
					isOpen={cutModal.isOpen}
					onClose={cutModal.onClose}
					videoId={videoId}
				/>
			)}
		</div>
	);
}
